import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import PropTypes from 'prop-types';
import './futures-graph-mobile.m.css';
import Loader from '../../../../atoms/icons/loader';
import { useQuery } from 'react-query';
import SearchFuturesOptions from './search-stocks';
import EmptyState from '../empty-state';
import isEmpty from 'lodash/isEmpty';
import { BQLink } from '../../../../atoms/bqlink';
import { OpenInNewIcon } from '../../../../atoms/material-icons';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';

const FuturesGraphMobile = ({
  maintainAspectRatio = true,
  responsive = true,
  flag = '',
  widget = false,
  container = '',
}) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [futuresOptionData, setFuturesOptionsData] = useState({});
  const [futuresChartData, setFuturesChartData] = useState({});
  const requiredLabels = [];
  const callOfChangeData = [];
  const putOfChangeData = [];
  const callsData = [];
  const putsData = [];
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState('NIFTY 50');
  const [selectedDuration, setSelectedDuration] = useState('1M');
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('change');
  const [dataForInterestChange, setDataForInterestChange] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [queryKey, setQueryKey] = useState('getFuturesOptionsChangeChartData');
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const getFuturesOptionOverallDataForQuery = async (stockName) => {
    const response = await fetch(`/foapi/future/getSummary?exchangeSymbol=${stockName}`);
    return response.json();
  };

  const getFuturesOptionChartDataForQuery = async (stockName, duration) => {
    const response = await fetch(
      `/foapi/future/getDistribution?exchangeSymbol=${stockName}&type=${duration}`,
    );
    return response.json();
  };

  const resultFuturesOptionsSummary = useQuery(
    'getFuturesOptionsSummary',
    () => getFuturesOptionOverallDataForQuery(selectedStock),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setFuturesOptionsData(data?.data);
      },
      enabled:
        initialLoading ||
        (!!selectedStock && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  const resultFuturesOptionsChartData = useQuery(
    queryKey,
    () => getFuturesOptionChartDataForQuery(selectedStock, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setFuturesChartData(data);
        generateChartData(data);
      },
      enabled:
        initialLoading ||
        (!!selectedStock &&
          !!selectedDuration &&
          isWithinTradingHours(tradingHolidays, specialTradingDays)),
      cacheTime: 0,
      retry: false,
    },
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultFuturesOptionsSummary?.isSuccess) {
      setFuturesOptionsData(resultFuturesOptionsSummary?.data?.data);
    }

    if (resultFuturesOptionsChartData?.isSuccess) {
      setFuturesChartData(resultFuturesOptionsChartData?.data);
    }

    generateChartData(resultFuturesOptionsChartData?.data);
    setDataForInterestChange(getDataForInterestChange('change'));
  }, [
    resultFuturesOptionsSummary?.isSuccess,
    resultFuturesOptionsChartData?.isSuccess,
    selectedDuration,
    timePeriod,
  ]);

  const generateChartData = (data) => {
    data &&
      data?.data
        ?.filter((chartValue) => {
          return chartValue.type === 'call';
        })
        .map((filteredValue) => {
          requiredLabels.push(filteredValue['strike-price']);
          callOfChangeData.push(filteredValue['open-interest-change']);
          callsData.push(filteredValue['open-interest']);
        });

    data &&
      data?.data
        ?.filter((chartValue) => {
          return chartValue.type === 'put';
        })
        .map((filteredValue) => {
          putOfChangeData.push(filteredValue['open-interest-change']);
          putsData.push(filteredValue['open-interest']);
        });
  };

  const options = {
    animation: false,
    responsive: responsive,
    maintainAspectRatio: maintainAspectRatio,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: isDarkMode && widget ? '#FFFFFF' : '#666',
        },
      },
      title: {
        display: true,
        text: '',
      },
      tooltip: {
        intersect: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: isDarkMode && widget ? '#FFFFFF' : '#666',
        },
        grid: {
          color: isDarkMode && widget ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        ticks: {
          color: isDarkMode && widget ? '#FFFFFF' : '#666',
        },
        grid: {
          color: isDarkMode && widget ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  };

  const getDataForInterestChange = (tab) => {
    const callLabel = tab === 'change' ? 'Call OI Change' : 'Calls';
    const putLabel = tab === 'change' ? 'Put OI Change' : 'Puts';
    const callData = tab === 'change' ? callOfChangeData : callsData;
    const putData = tab === 'change' ? putOfChangeData : putsData;
    return {
      labels: requiredLabels,
      datasets: [
        {
          label: callLabel,
          data: callData,
          backgroundColor: '#14C588',
        },
        {
          label: putLabel,
          data: putData,
          backgroundColor: '#d01e51',
        },
      ],
    };
  };

  const interestDistChangeHandler = (type, key) => {
    setQueryKey(key);
    setCurrentTab(type);
    setTimePeriod('monthly');
    setSelectedDuration('1M');
    durationChangeHandler('1M', true, type);
  };

  const timePeriodHandler = (event) => {
    const period =
      event.target.value === 'monthly' ? '1M' : event.target.value === 'weekly' ? '1W' : '1C';
    durationChangeHandler(period);
    setTimePeriod(event.target.value);
    setSelectedDuration(period);
  };

  const durationChangeHandler = async (timeValueSelected, switchChart = false, chartType) => {
    setLoading(true);
    const data = await getFuturesOptionChartDataForQuery(selectedStock, timeValueSelected);
    setFuturesChartData(data);
    if (switchChart) {
      generateChartData(data);
      setDataForInterestChange(getDataForInterestChange(chartType));
    }
    setLoading(false);
  };

  const getFuturesOptionOverallData = async (stockName) => {
    setLoading(true);
    const data = await getFuturesOptionOverallDataForQuery(stockName);
    setFuturesOptionsData(data?.data);
    setLoading(false);
  };

  const getFuturesOptionsChartData = async (stockName, duration) => {
    setLoading(true);
    const data = await getFuturesOptionChartDataForQuery(stockName, duration);
    setFuturesChartData(data);
    setLoading(false);
  };

  const stockSearchHandler = (stockName) => {
    setSelectedStock(stockName);
    const formatedStockName = stockName.replace(' ', '%20');
    getFuturesOptionOverallData(formatedStockName);
    getFuturesOptionsChartData(formatedStockName, selectedDuration);
    setTimePeriod('monthly');
  };

  const stockSearchCloseHandler = (defaultStatus) => {
    if (defaultStatus) {
      setSelectedStock('NIFTY 50');
      setSelectedDuration('1M');
      durationChangeHandler('1M');
    }
  };

  const summaryKeymaps = [
    { key: 'basis', label: 'Basis' },
    { key: '1m-future', label: '1M Futures' },
    { key: 'roll-over-percentage', label: 'Rollover%' },
    { key: 'put-call-ratio', label: 'Put Call Ratio' },
  ];

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div>
          {(isEmpty(futuresOptionData) && isEmpty(futuresChartData)) ||
          (!futuresOptionData && !futuresChartData) ? (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          ) : (
            <div styleName={`${widget ? 'widget-container' : ''}`}>
              <div styleName="futures-header">
                <div styleName="letter-container">
                  <span styleName="letter">{selectedStock.toString().charAt(0)}</span>
                </div>
                <div styleName="overall-stats-container">
                  <SearchFuturesOptions
                    onStockSearch={stockSearchHandler}
                    onCloseSearch={stockSearchCloseHandler}
                  />

                  <div styleName="statistics-container">
                    {summaryKeymaps.map((keymap, index) => (
                      <div styleName="stats" key={index}>
                        <p styleName="stat-value">
                          {futuresOptionData && futuresOptionData?.[keymap.key]
                            ? futuresOptionData?.[keymap.key]
                            : '-'}{' '}
                          <br /> <span styleName="stat-desc">{keymap.label}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div styleName="btn-container">
                <button
                  styleName={`futures-main-btn ${currentTab === 'change' ? 'selected' : ' '}`}
                  onClick={() =>
                    interestDistChangeHandler('change', 'getFuturesOptionsChangeChartData')
                  }
                >
                  OI Change
                </button>
                <button
                  styleName={`futures-main-btn ${currentTab === 'distribution' ? 'selected' : ' '}`}
                  onClick={() =>
                    interestDistChangeHandler(
                      'distribution',
                      'getFuturesOptionsDistChangeChartData',
                    )
                  }
                >
                  OI Distribution
                </button>
              </div>
              <div styleName="filter-container">
                <div styleName="time-filters">
                  <button
                    styleName={`time-btn ${['1M', '1W', '1C'].includes(selectedDuration) ? 'selected' : ''}`}
                    onClick={() => {
                      durationChangeHandler(
                        timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C',
                      );
                      setSelectedDuration(
                        timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C',
                      );
                    }}
                  >
                    {timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C'}
                  </button>
                  <button
                    styleName={`time-btn ${['2M', '2W', '2C'].includes(selectedDuration) ? 'selected' : ''}`}
                    onClick={() => {
                      durationChangeHandler(
                        timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C',
                      );
                      setSelectedDuration(
                        timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C',
                      );
                    }}
                  >
                    {timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C'}
                  </button>
                  <button
                    styleName={`time-btn ${['3M', '3W', '3C'].includes(selectedDuration) ? 'selected' : ''}`}
                    onClick={() => {
                      durationChangeHandler(
                        timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C',
                      );
                      setSelectedDuration(
                        timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C',
                      );
                    }}
                  >
                    {timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C'}
                  </button>
                </div>

                <div styleName="dropdown-filter">
                  <select
                    id="time"
                    name="time"
                    styleName="select-btn"
                    onChange={timePeriodHandler}
                    value={timePeriod}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="cumulativeMonthly">Cumulative Monthly</option>
                  </select>
                </div>
              </div>
              <div styleName="graph-container">
                {loading ? (
                  <div styleName="loader-container-ext-chart">
                    <div styleName="no-data-container-int">
                      <Loader color={'#dadee1'} />
                    </div>
                  </div>
                ) : isEmpty(futuresChartData) ? (
                  <div styleName="no-data-container-ext-chart">
                    <div styleName="no-data-container-int">
                      <EmptyState />
                    </div>
                  </div>
                ) : (
                  <Bar
                    options={options}
                    data={dataForInterestChange}
                    height="250px"
                    width="300px"
                  />
                )}
              </div>
              {flag === 'homepage' && widget && container === 'full' && (
                <div styleName="external-btn-container">
                  <BQLink
                    styleName="external-btn"
                    href={widgetLink(`/markets/futures-contract${widgetParam}`, widget)}
                    target="_blank"
                  >
                    Futures <OpenInNewIcon color={'#d01e51'} styleName="tab-icon" />
                  </BQLink>
                  <BQLink
                    styleName="external-btn"
                    href={widgetLink(`/markets/option-chain${widgetParam}`, widget)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Options <OpenInNewIcon color={'#d01e51'} styleName="tab-icon" />
                  </BQLink>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

FuturesGraphMobile.propTypes = {
  maintainAspectRatio: PropTypes.bool,
  responsive: PropTypes.bool,
};

export default FuturesGraphMobile;
